import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, CalendarPlus, Clock, AlertTriangle, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PromoShareSubpanel } from "./TempPromoModal";
import { MAX_EXTRA_DAYS, EXPIRED_GRACE_DAYS, graceDeadline } from "@/utils/promoState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtLocal = (iso) => {
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

/**
 * PromoExtendModal — prórroga de una promoción temporal activa (1..7 días
 * extra acumulados). Envía imagen + texto al chat del cliente y muestra el
 * panel de compartir por WhatsApp con el mismo mensaje.
 */
export const PromoExtendModal = ({ plan, promo, clientUser = null, authHeader, onClose, onExtended }) => {
  const used = Number(promo?.extended_days_total || 0);
  const left = Math.max(0, MAX_EXTRA_DAYS - used);
  const [days, setDays] = useState(Math.min(2, left) || 1);
  const [customMessage, setCustomMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shareData, setShareData] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  if (!plan || !promo) return null;
  const currency = plan.currency || "MXN";
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  const untilMs = new Date(promo.valid_until).getTime();
  const isExpired = untilMs <= Date.now();
  const daysSinceExpiry = isExpired ? Math.floor((Date.now() - untilMs) / 86400000) : 0;
  const withinGrace = !isExpired || (Date.now() - untilMs) <= EXPIRED_GRACE_DAYS * 86400000;
  // Active: extends from current end. Expired: new validity counts from NOW.
  const newUntil = new Date(Math.max(untilMs, Date.now()) + days * 86400000);
  const canExtend = left > 0 && withinGrace;

  const submit = async () => {
    setError("");
    if (days < 1 || days > left) return setError(`Puedes agregar entre 1 y ${left} día(s)`);
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/payment-plans/${plan.id}/promotions/${promo.id}/extend`,
        { extra_days: days, ...(customMessage.trim() ? { custom_message: customMessage.trim() } : {}) },
        { headers: authHeader() },
      );
      toast.success(r.data.reactivated ? `Promoción reactivada por ${days} día(s)` : `Prórroga de ${days} día(s) enviada al cliente`);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const shareUrl = `${backend}${r.data.share_path}`;
      setShareData({
        message: `${r.data.extension.chat_message}\n\n${shareUrl}`,
        share_url: shareUrl,
        image_url: `${backend}${r.data.image_path}`,
        client_name: clientUser?.name || clientUser?.username || plan.user_name || "cliente",
        client_phone: clientUser?.phone || plan.user_phone || "",
      });
      onExtended?.(r.data.promotion);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo prorrogar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[160] bg-black/85 backdrop-blur-md flex items-center justify-center px-3 py-6 overflow-y-auto"
        data-testid="promo-extend-modal"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-lg bg-gradient-to-br from-[#0a0510] to-black border-2 border-sky-400/60 rounded-2xl p-5 my-auto shadow-[0_0_60px_rgba(56,189,248,0.25)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button data-testid="promo-extend-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>

          {shareData ? (
            <PromoShareSubpanel data={shareData} onClose={onClose} title={isExpired ? "Promoción reactivada" : "Prórroga enviada"} subtitle="El cliente ya recibió la imagen y el mensaje en su chat. Compártela también por WhatsApp:" />
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                {isExpired ? <RotateCcw className="w-5 h-5 text-amber-300" /> : <CalendarPlus className="w-5 h-5 text-sky-300" />}
                <h3 className="font-serif text-lg text-white">{isExpired ? "Reactivar promoción vencida" : "Prorrogar promoción"}</h3>
              </div>
              <p className="text-xs text-neutral-400 mb-3">
                {isExpired
                  ? <>La promoción venció, pero aún está dentro del plazo de gracia de {EXPIRED_GRACE_DAYS} días. Al prorrogarla <b className="text-white">se reactiva</b> y la nueva vigencia se cuenta <b className="text-white">desde este momento</b>. El cliente recibe imagen y mensaje con la nueva fecha.</>
                  : <>Extiende la vigencia de la promoción activa. El cliente recibe una imagen de prórroga con la nueva fecha y el aviso de que <b className="text-white">al vencer ya no habrá otra promoción</b>.</>}
              </p>

              {isExpired && (
                <div data-testid="promo-extend-expired-notice" className={`mb-3 rounded-lg border p-2.5 text-[11px] flex items-start gap-2 ${withinGrace ? "border-amber-400/40 bg-amber-500/10 text-amber-100" : "border-red-400/40 bg-red-500/10 text-red-200"}`}>
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Venció el <b>{fmtLocal(promo.valid_until)}</b> (hace {daysSinceExpiry} día{daysSinceExpiry !== 1 ? "s" : ""}).
                    {withinGrace
                      ? <> Puedes reactivarla hasta el <b>{fmtLocal(graceDeadline(promo).toISOString())}</b>.</>
                      : <> Pasaron más de {EXPIRED_GRACE_DAYS} días — ya no se puede prorrogar; crea una promoción nueva.</>}
                  </span>
                </div>
              )}

              <div className="rounded-lg bg-black/40 border border-white/10 p-3 mb-3 text-xs space-y-1">
                <div className="text-neutral-400">Cliente: <b className="text-white">{clientUser?.name || plan.user_name}</b></div>
                <div className="text-neutral-400">Trabajo: <b className="text-white">{plan.service_name}</b></div>
                <div className="text-neutral-400">Descuento: <b className="text-amber-300">{promo.kind === "percentage" ? `${promo.value}%` : fmtMoney(promo.value)}</b> · Nuevo total <b className="text-emerald-300">{fmtMoney(promo.new_remaining)}</b></div>
                <div className="text-neutral-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {isExpired ? "Venció" : "Vence actualmente"}: <b className="text-white">{fmtLocal(promo.valid_until)}</b></div>
              </div>

              <div className="mb-3 flex items-center justify-between text-[11px]">
                <span className="text-neutral-400">Días extra usados: <b className="text-white">{used}/{MAX_EXTRA_DAYS}</b></span>
                <span className={left > 0 ? "text-sky-300" : "text-red-300"}>{left > 0 ? `Puedes agregar hasta ${left}` : "Límite alcanzado"}</span>
              </div>

              {canExtend ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-sky-300/80 mb-1.5">Días extra</div>
                  <div className="grid grid-cols-7 gap-1.5 mb-3">
                    {Array.from({ length: MAX_EXTRA_DAYS }, (_, i) => i + 1).map((d) => (
                      <button
                        key={d}
                        type="button"
                        disabled={d > left}
                        data-testid={`promo-extend-day-${d}`}
                        onClick={() => setDays(d)}
                        className={`py-2 rounded text-sm font-bold border transition ${days === d ? "bg-sky-500/25 border-sky-400 text-sky-200" : "bg-black/40 border-white/10 text-neutral-400 hover:border-sky-400/40"} disabled:opacity-25 disabled:cursor-not-allowed`}
                      >
                        +{d}
                      </button>
                    ))}
                  </div>

                  <div className="mb-3 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-1">Nueva fecha límite</div>
                    <div className="text-white font-semibold" data-testid="promo-extend-new-date">{fmtLocal(newUntil.toISOString())}</div>
                    <div className="text-neutral-400 mt-0.5">+{days} día{days !== 1 ? "s" : ""} {isExpired ? "a partir de ahora (reactivación)" : "sobre la vigencia actual"}</div>
                  </div>

                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">Mensaje personalizado (opcional)</div>
                    <textarea
                      data-testid="promo-extend-message"
                      rows={2}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Si lo dejas vacío, se envía el mensaje de prórroga predeterminado (incluye el aviso de que no habrá otra promoción)."
                      className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-xs focus:outline-none focus:border-sky-400"
                    />
                  </div>

                  {error && <div className="mb-3 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>}

                  <button
                    type="button"
                    data-testid="promo-extend-submit"
                    disabled={busy}
                    onClick={submit}
                    className={`w-full py-2.5 rounded-full flex items-center justify-center gap-1.5 uppercase text-[10px] tracking-widest font-bold text-black disabled:opacity-50 ${isExpired ? "bg-amber-400 hover:bg-amber-300" : "bg-sky-500 hover:bg-sky-400"}`}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isExpired ? <RotateCcw className="w-3.5 h-3.5" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                    {isExpired ? "Reactivar" : "Enviar prórroga"} de {days} día{days !== 1 ? "s" : ""}
                  </button>
                </>
              ) : (
                <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-[11px] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-100">
                    {left <= 0
                      ? `Esta promoción ya usó los ${MAX_EXTRA_DAYS} días extra permitidos. Cuando venza, podrás crear una promoción nueva si lo decides.`
                      : `El plazo de gracia de ${EXPIRED_GRACE_DAYS} días ya pasó. Crea una promoción nueva para este trabajo.`}
                  </span>
                </div>
              )}

              {(promo.extensions || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Prórrogas anteriores ({promo.extensions.length})</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                    {[...promo.extensions].reverse().map((e) => (
                      <div key={e.id} className="text-[10px] flex justify-between gap-2 bg-black/40 rounded px-2 py-1.5" data-testid={`promo-ext-row-${e.id}`}>
                        <span className="text-sky-300 font-semibold">+{e.extra_days} día{e.extra_days !== 1 ? "s" : ""}</span>
                        <span className="text-neutral-400 truncate">→ {fmtLocal(e.new_valid_until)}</span>
                        <span className="text-neutral-600 whitespace-nowrap">{e.by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PromoExtendModal;
